import { useState, useEffect } from 'react';
import {
  Layout,
  Button,
  Modal,
  Form,
  Upload,
  Alert,
  message,
  Progress,
  List,
  Tag,
  Select,
  Space,
  Typography,
  Table,
  Tabs
} from 'antd';
import {
  UploadOutlined,
  DownloadOutlined,
  ReloadOutlined,
  ArrowRightOutlined,
  EyeOutlined
} from '@ant-design/icons';
import Papa from 'papaparse';

import Sidebar from '../components/Sidebar';
import AppHeader from '../components/Header';
import { encryptData, decryptData } from '../middleware/encryption';

const { Content } = Layout;
const { Option } = Select;
const { Text } = Typography;
const { TabPane } = Tabs;

export default function ImportData() {
  const [collapsed, setCollapsed] = useState(false);
  const [isLeadsModalVisible, setIsLeadsModalVisible] = useState(false);
  const [isCompanyModalVisible, setIsCompanyModalVisible] = useState(false);
  const [isMappingModalVisible, setIsMappingModalVisible] = useState(false);
  const [isPreviewModalVisible, setIsPreviewModalVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showProgress, setShowProgress] = useState(false);
  const [chunkStatus, setChunkStatus] = useState([]);
  const [csvDataChunks, setCsvDataChunks] = useState([]);
  const [failedChunksData, setFailedChunksData] = useState([]);
  const [uploadEndpoint, setUploadEndpoint] = useState('');
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [csvPreview, setCsvPreview] = useState([]);
  const [fieldMappings, setFieldMappings] = useState({});
  const [importType, setImportType] = useState('');
  const [mappedPreviewData, setMappedPreviewData] = useState([]);
  const [originalPreviewData, setOriginalPreviewData] = useState([]);
  const [form] = Form.useForm();

  // Define the database fields for leads import
  const leadsDatabaseFields = [
    { key: 'first_name', label: 'First Name', required: true },
    { key: 'last_name', label: 'Last Name', required: true },
    { key: 'title', label: 'Title', required: false },
    { key: 'company', label: 'Company', required: false },
    { key: 'email', label: 'Email', required: true },
    { key: 'email_status', label: 'Email Status', required: false },
    { key: 'seniority', label: 'Seniority', required: false },
    { key: 'departments', label: 'Departments', required: false },
    { key: 'work_direct_phone', label: 'Work Direct Phone', required: false },
    { key: 'mobile_phone', label: 'Mobile Phone', required: false },
    { key: 'corporate_phone', label: 'Corporate Phone', required: false },
    { key: 'phone', label: 'Phone', required: false },
    { key: 'category', label: 'Category', required: false },
    { key: 'organization', label: 'Organization', required: false },
    { key: 'position', label: 'Position', required: false },
    { key: 'country', label: 'Country', required: false },
    { key: 'city', label: 'City', required: false },
    { key: 'state', label: 'State', required: false },
    { key: 'company_address', label: 'Company Address', required: false },
    { key: 'company_city', label: 'Company City', required: false },
    { key: 'company_state', label: 'Company State', required: false },
    { key: 'company_country', label: 'Company Country', required: false },
    { key: 'industry', label: 'Industry', required: false },
    { key: 'num_employees', label: 'Number of Employees', required: false },
    { key: 'linkedin_url', label: 'LinkedIn URL', required: false },
    { key: 'website', label: 'Website', required: false },
    { key: 'company_linkedin_url', label: 'Company LinkedIn URL', required: false },
    { key: 'facebook_url', label: 'Facebook URL', required: false },
    { key: 'twitter_url', label: 'Twitter URL', required: false },
    { key: 'keywords', label: 'Keywords', required: false },
    { key: 'technologies', label: 'Technologies', required: false },
    { key: 'seo_description', label: 'SEO Description', required: false },
    { key: 'annual_revenue', label: 'Annual Revenue', required: false },
    { key: 'total_funding', label: 'Total Funding', required: false },
    { key: 'latest_funding', label: 'Latest Funding', required: false },
    { key: 'latest_funding_amount', label: 'Latest Funding Amount', required: false },
    { key: 'last_raised_at', label: 'Last Raised At', required: false },
    { key: 'num_retail_locations', label: 'Number of Retail Locations', required: false },
    { key: 'domain', label: 'Domain', required: false },
    { key: 'country_norm', label: 'Country Norm', required: false }
  ];

  // Define the database fields for company import
  const companyDatabaseFields = [
    { key: 'company_name', label: 'Company Name', required: true },
    { key: 'num_employees', label: 'Number of Employees', required: false },
    { key: 'industry', label: 'Industry', required: false },
    { key: 'website', label: 'Website', required: false },
    { key: 'company_linkedin_url', label: 'Company LinkedIn URL', required: false },
    { key: 'facebook_url', label: 'Facebook URL', required: false },
    { key: 'twitter_url', label: 'Twitter URL', required: false },
    { key: 'company_street', label: 'Company Street', required: false },
    { key: 'company_city', label: 'Company City', required: false },
    { key: 'company_state', label: 'Company State', required: false },
    { key: 'company_country', label: 'Company Country', required: false },
    { key: 'company_postal_code', label: 'Company Postal Code', required: false },
    { key: 'company_address', label: 'Company Address', required: false },
    { key: 'keywords', label: 'Keywords', required: false },
    { key: 'company_phone', label: 'Company Phone', required: false },
    { key: 'seo_description', label: 'SEO Description', required: false },
    { key: 'technologies', label: 'Technologies', required: false },
    { key: 'total_funding', label: 'Total Funding', required: false },
    { key: 'latest_funding', label: 'Latest Funding', required: false },
    { key: 'latest_funding_amount', label: 'Latest Funding Amount', required: false },
    { key: 'last_raised_at', label: 'Last Raised At', required: false },
    { key: 'annual_revenue', label: 'Annual Revenue', required: false },
    { key: 'num_retail_locations', label: 'Number of Retail Locations', required: false },
    { key: 'sic_codes', label: 'SIC Codes', required: false },
    { key: 'short_description', label: 'Short Description', required: false },
    { key: 'founded_year', label: 'Founded Year', required: false }
  ];

  useEffect(() => {
    const beforeUnload = (e) => {
      if (loading) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', beforeUnload);
    return () => window.removeEventListener('beforeunload', beforeUnload);
  }, [loading]);

  const showLeadsModal = () => {
    setUploadEndpoint('https://upload.exellius.com/api/leads/addPeopleLeadsData/');
    setImportType('leads');
    setIsLeadsModalVisible(true);
  };

  const showCompanyModal = () => {
    setUploadEndpoint('https://upload.exellius.com/api/leads/addCompaniesData/');
    setImportType('company');
    setIsCompanyModalVisible(true);
  };

  const handleCancel = () => {
    setIsLeadsModalVisible(false);
    setIsCompanyModalVisible(false);
    setIsMappingModalVisible(false);
    setIsPreviewModalVisible(false);
    setFileList([]);
    setShowProgress(false);
    setUploadProgress(0);
    setChunkStatus([]);
    setCsvDataChunks([]);
    setFailedChunksData([]);
    setCsvHeaders([]);
    setCsvPreview([]);
    setFieldMappings({});
    setMappedPreviewData([]);
    setOriginalPreviewData([]);
    form.resetFields();
  };

  // Function to normalize field names for comparison
  const normalizeFieldName = (field) => {
    return field
      .toLowerCase()
      .replace(/[_\s-]+/g, '')
      .replace(/[^\w]/g, '');
  };

  // Function to find best match for a database field in CSV headers
  const findBestMatch = (dbField, csvHeaders) => {
    const normalizedDbField = normalizeFieldName(dbField.key);
    
    // First, try exact match
    const exactMatch = csvHeaders.find(header => 
      header.toLowerCase() === dbField.key.toLowerCase() ||
      header.toLowerCase() === dbField.label.toLowerCase()
    );
    if (exactMatch) return exactMatch;

    // Then try normalized match
    for (const header of csvHeaders) {
      const normalizedHeader = normalizeFieldName(header);
      if (normalizedHeader === normalizedDbField) {
        return header;
      }
    }

    // Try partial matches
    for (const header of csvHeaders) {
      const normalizedHeader = normalizeFieldName(header);
      if (normalizedHeader.includes(normalizedDbField) || normalizedDbField.includes(normalizedHeader)) {
        return header;
      }
    }

    return null;
  };

  const handleFileChange = ({ fileList }) => {
    const file = fileList[0]?.originFileObj;
    if (!file) return;
    
    const isCSV = file.name.endsWith('.csv');
    if (!isCSV) {
      message.error('Only .csv files are allowed!');
      return;
    }
    
    setFileList(fileList.slice(-1));
    
    // Parse CSV headers for mapping
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      preview: 10,
      complete: function (results) {
        const headers = results.meta.fields || [];
        setCsvHeaders(headers);
        setCsvPreview(results.data.slice(0, 5));
        setOriginalPreviewData(results.data.slice(0, 5));
        
        // Auto-map fields based on best match
        const databaseFields = importType === 'leads' ? leadsDatabaseFields : companyDatabaseFields;
        const initialMappings = {};
        
        databaseFields.forEach(dbField => {
          const bestMatch = findBestMatch(dbField, headers);
          if (bestMatch) {
            initialMappings[dbField.key] = bestMatch;
          }
        });
        
        console.log('Best matched fields:', initialMappings);
        setFieldMappings(initialMappings);
        
        // Generate mapped preview data
        generateMappedPreview(results.data.slice(0, 5), initialMappings);
      },
      error: function (error) {
        console.error(error);
        message.error('Error parsing CSV headers');
      }
    });
  };

  const generateMappedPreview = (data, mappings) => {
    const reverseMapping = {};
    Object.entries(mappings).forEach(([dbField, csvField]) => {
      if (csvField) {
        reverseMapping[csvField] = dbField;
      }
    });

    const mappedData = data.map(row => {
      const mappedRow = {};
      
      Object.entries(row).forEach(([csvField, value]) => {
        const dbField = reverseMapping[csvField];
        if (dbField && value) {
          mappedRow[dbField] = value;
        }
      });
      
      return mappedRow;
    }).filter(row => Object.keys(row).length > 0);

    setMappedPreviewData(mappedData);
  };

  const handleMappingChange = (dbField, csvField) => {
    const updatedMappings = {
      ...fieldMappings,
      [dbField]: csvField
    };
    setFieldMappings(updatedMappings);
    generateMappedPreview(originalPreviewData, updatedMappings);
  };

  const openMappingModal = () => {
    if (fileList.length === 0) {
      message.warning('Please select a CSV file first');
      return;
    }
    setIsMappingModalVisible(true);
  };

  const openPreviewModal = () => {
    if (Object.keys(fieldMappings).length === 0) {
      message.warning('Please configure field mappings first');
      return;
    }
    setIsPreviewModalVisible(true);
  };

  const processMappedData = () => {
    const requiredFields = importType === 'leads' 
      ? leadsDatabaseFields.filter(f => f.required).map(f => f.key)
      : companyDatabaseFields.filter(f => f.required).map(f => f.key);
    
    const mappedDbFields = Object.keys(fieldMappings).filter(key => fieldMappings[key]);
    const missingRequiredFields = requiredFields.filter(
      field => !mappedDbFields.includes(field)
    );

    if (missingRequiredFields.length > 0) {
      message.error(`Please map required fields: ${missingRequiredFields.join(', ')}`);
      return;
    }

    setIsMappingModalVisible(false);
  };

  const parseAndUpload = async (endpoint) => {
    const file = fileList[0].originFileObj;
    const token = sessionStorage.getItem('access_token');

    setLoading(true);
    setUploadProgress(0);
    setShowProgress(true);
    setChunkStatus([]);
    setCsvDataChunks([]);
    setFailedChunksData([]);

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: async function (results) {
        const reverseMapping = {};
        Object.entries(fieldMappings).forEach(([dbField, csvField]) => {
          if (csvField) {
            reverseMapping[csvField] = dbField;
          }
        });

        const mappedRows = results.data.map(row => {
          const mappedRow = {};
          
          Object.entries(row).forEach(([csvField, value]) => {
            const dbField = reverseMapping[csvField];
            if (dbField && value) {
              mappedRow[dbField] = value;
            }
          });
          
          return mappedRow;
        }).filter(row => Object.keys(row).length > 0);

        console.log('Total records to upload:', mappedRows.length);

        const chunkSize = 200;
        const chunks = [];

        for (let i = 0; i < mappedRows.length; i += chunkSize) {
          chunks.push(mappedRows.slice(i, i + chunkSize));
        }

        console.log(`Split into ${chunks.length} chunks of up to ${chunkSize} records each`);
        
        setCsvDataChunks(chunks);
        setChunkStatus(chunks.map(() => ({ status: 'pending' })));

        await uploadChunks(chunks, endpoint, token);
      },
      error: function (error) {
        console.error(error);
        setLoading(false);
        message.error('Error parsing CSV');
      }
    });
  };

  const uploadChunks = async (chunks, endpoint, token, retryIndexes = null) => {
    const allData = chunks.flat();
    console.log('Total records to upload:', allData.length);
    
    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    };

    const results = [...chunkStatus];
    const retrying = retryIndexes !== null;
    
    const chunksToUpload = retrying 
      ? chunks.filter((_, index) => retryIndexes.includes(index))
      : chunks;
    
    const chunkIndices = retrying
      ? retryIndexes
      : Array.from({ length: chunks.length }, (_, i) => i);
    
    chunkIndices.forEach(idx => {
      results[idx] = { status: 'uploading' };
    });
    setChunkStatus([...results]);

    const uploadPromises = chunksToUpload.map(async (chunk, taskIndex) => {
      const originalIndex = chunkIndices[taskIndex];
      
      try {
        console.log(`Uploading chunk ${originalIndex + 1} with ${chunk.length} records`);
        
        const response = await fetch(endpoint, {
          method: 'POST',
          headers,
          body: JSON.stringify({ data: encryptData(chunk) })
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(errorText);
        }

        results[originalIndex] = { status: 'success' };
        console.log(`Chunk ${originalIndex + 1} uploaded successfully`);
      } catch (error) {
        console.error(`Chunk ${originalIndex + 1} failed:`, error);
        results[originalIndex] = { status: 'failed', error: error.message };
        setFailedChunksData((prev) => [...prev, ...chunk]);
      }
      
      setChunkStatus([...results]);
      const completed = results.filter(
        (r) => r.status !== 'pending' && r.status !== 'uploading'
      ).length;
      setUploadProgress(Math.round((completed / chunks.length) * 100));
    });

    await Promise.all(uploadPromises);

    const anyFailed = results.some((r) => r.status === 'failed');
    if (!anyFailed) {
      message.success('CSV imported successfully');
      setIsLeadsModalVisible(false);
      setIsCompanyModalVisible(false);
      setFileList([]);
      setShowProgress(false);
    } else {
      message.warning('Some chunks failed. You can retry them or download the failed data.');
    }

    setLoading(false);
  };

  const retryFailedChunks = () => {
    const failedIndexes = chunkStatus
      .map((chunk, index) => (chunk.status === 'failed' ? index : null))
      .filter((index) => index !== null);

    if (failedIndexes.length > 0) {
      setLoading(true);
      uploadChunks(
        csvDataChunks,
        uploadEndpoint,
        sessionStorage.getItem('access_token'),
        failedIndexes
      );
    }
  };

  const downloadFailedChunks = () => {
    if (!failedChunksData.length) return;

    const csv = Papa.unparse(failedChunksData);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'failed_chunks.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const downloadSampleData = () => {
    const link = document.createElement('a');
    link.href = '/company_template.csv';
    link.download = 'company_template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadSampleDataPeople = () => {
    const link = document.createElement('a');
    link.href = '/people_template.csv';
    link.download = 'people_template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const renderStatus = (status) => {
    switch (status) {
      case 'pending':
        return <Tag color="default">Pending</Tag>;
      case 'uploading':
        return <Tag color="processing">Uploading</Tag>;
      case 'success':
        return <Tag color="green">Success</Tag>;
      case 'failed':
        return <Tag color="red">Failed</Tag>;
      default:
        return null;
    }
  };

  const renderMappingModal = () => {
    const databaseFields = importType === 'leads' ? leadsDatabaseFields : companyDatabaseFields;
    
    return (
      <Modal
        title="Map Database Fields to CSV Columns"
        visible={isMappingModalVisible}
        onOk={processMappedData}
        onCancel={() => setIsMappingModalVisible(false)}
        width={900}
        okText="Save Mapping"
        cancelText="Back"
      >
        <Alert
          message="Map your database fields to the corresponding CSV columns. Required fields are marked with *."
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
        
        {csvPreview.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <Text strong>CSV Preview (first 5 rows):</Text>
            <Table
              size="small"
              dataSource={csvPreview.map((row, idx) => ({ ...row, key: idx }))}
              columns={csvHeaders.map(header => ({
                title: header,
                dataIndex: header,
                key: header,
                ellipsis: true,
                width: 150
              }))}
              scroll={{ x: 'max-content' }}
              pagination={false}
            />
          </div>
        )}

        <div style={{ maxHeight: 400, overflowY: 'auto' }}>
          <Table
            size="middle"
            dataSource={databaseFields.map((field, index) => ({
              key: index,
              dbField: field.key,
              dbLabel: field.label,
              required: field.required,
              mappedTo: fieldMappings[field.key] || ''
            }))}
            pagination={false}
          >
            <Table.Column
              title="Database Field"
              key="dbField"
              width="40%"
              render={(_, record) => (
                <Text>
                  {record.dbLabel} {record.required && <Text type="danger">*</Text>}
                </Text>
              )}
            />
            <Table.Column
              title="Map from CSV Column"
              key="mapping"
              width="60%"
              render={(_, record) => (
                <Space>
                  <ArrowRightOutlined />
                  <Select
                    style={{ width: 300 }}
                    placeholder="Select CSV column"
                    value={record.mappedTo || undefined}
                    onChange={(value) => handleMappingChange(record.dbField, value)}
                    allowClear
                  >
                    {csvHeaders.map(header => (
                      <Option key={header} value={header}>
                        {header}
                      </Option>
                    ))}
                  </Select>
                </Space>
              )}
            />
          </Table>
        </div>

        <div style={{ marginTop: 16 }}>
          <Text type="secondary">
            <strong>Auto-matching:</strong> Fields have been automatically matched where possible using fuzzy matching.
            Please review and adjust as needed.
          </Text>
        </div>
      </Modal>
    );
  };

  const renderPreviewModal = () => {
    const databaseFields = importType === 'leads' ? leadsDatabaseFields : companyDatabaseFields;
    const mappedColumns = Object.values(fieldMappings).filter(v => v);
    
    const previewColumns = databaseFields
      .filter(field => fieldMappings[field.key])
      .map(field => ({
        title: `${field.label} ${field.required ? '*' : ''}`,
        dataIndex: field.key,
        key: field.key,
        ellipsis: true,
        width: 150,
        render: (text) => text || <Text type="secondary">-</Text>
      }));

    return (
      <Modal
        title="Preview Mapped Data"
        visible={isPreviewModalVisible}
        onCancel={() => setIsPreviewModalVisible(false)}
        width={1200}
        footer={[
          <Button key="back" onClick={() => setIsPreviewModalVisible(false)}>
            Close
          </Button>,
          <Button
            key="import"
            type="primary"
            onClick={() => {
              setIsPreviewModalVisible(false);
              parseAndUpload(uploadEndpoint);
            }}
          >
            Proceed to Import
          </Button>
        ]}
      >
        <Tabs defaultActiveKey="1">
          <TabPane tab="Mapped Data Preview" key="1">
            <Alert
              message={`Showing preview of first ${mappedPreviewData.length} rows with mapped fields. Total mapped fields: ${mappedColumns.length}`}
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
            
            {mappedPreviewData.length > 0 ? (
              <Table
                size="small"
                dataSource={mappedPreviewData.map((row, idx) => ({ ...row, key: idx }))}
                columns={previewColumns}
                scroll={{ x: 'max-content', y: 400 }}
                pagination={false}
              />
            ) : (
              <Alert message="No mapped data to preview" type="warning" />
            )}
          </TabPane>
          
          <TabPane tab="Mapping Summary" key="2">
            <Table
              size="middle"
              dataSource={databaseFields
                .filter(field => fieldMappings[field.key])
                .map((field, index) => ({
                  key: index,
                  dbField: field.label,
                  csvColumn: fieldMappings[field.key],
                  required: field.required ? 'Yes' : 'No'
                }))}
              pagination={false}
            >
              <Table.Column title="Database Field" dataIndex="dbField" key="dbField" />
              <Table.Column title="CSV Column" dataIndex="csvColumn" key="csvColumn" />
              <Table.Column title="Required" dataIndex="required" key="required" />
            </Table>
            
            {databaseFields
              .filter(field => field.required && !fieldMappings[field.key])
              .length > 0 && (
              <Alert
                message="Warning: Some required fields are not mapped"
                description={databaseFields
                  .filter(field => field.required && !fieldMappings[field.key])
                  .map(field => field.label)
                  .join(', ')}
                type="warning"
                showIcon
                style={{ marginTop: 16 }}
              />
            )}
          </TabPane>
        </Tabs>
      </Modal>
    );
  };

  const renderModalContent = (downloadFn) => (
    <>
      {showProgress && (
        <>
          <Progress
            percent={uploadProgress}
            status={uploadProgress === 100 ? 'success' : 'active'}
            style={{ marginBottom: 16 }}
          />
          <List
            size="small"
            header={<b>Chunk Upload Status</b>}
            bordered
            dataSource={chunkStatus}
            renderItem={(item, index) => (
              <List.Item>
                Chunk {index + 1}: {renderStatus(item.status)}
              </List.Item>
            )}
            style={{ maxHeight: 200, overflowY: 'scroll', marginBottom: 16 }}
          />
          {chunkStatus.some((c) => c.status === 'failed') && (
            <Space>
              <Button
                icon={<ReloadOutlined />}
                onClick={retryFailedChunks}
                loading={loading}
              >
                Retry Failed Chunks
              </Button>
              <Button onClick={downloadFailedChunks}>Download Failed Chunks CSV</Button>
            </Space>
          )}
        </>
      )}

      {!showProgress && (
        <>
          <Form form={form} layout="vertical">
            <Form.Item
              name="file"
              label="CSV File"
              rules={[{ required: true, message: 'Please select a CSV file!' }]}
            >
              <Upload
                beforeUpload={() => false}
                fileList={fileList}
                onChange={handleFileChange}
                accept=".csv"
              >
                <Button icon={<UploadOutlined />}>Select File</Button>
              </Upload>
            </Form.Item>
            
            {csvHeaders.length > 0 && (
              <Alert
                message={`Detected ${csvHeaders.length} columns in CSV. ${
                  Object.keys(fieldMappings).length > 0 
                    ? `${Object.keys(fieldMappings).length} fields auto-matched.` 
                    : 'Click "Map Columns" to configure field mappings.'
                }`}
                type="success"
                showIcon
                style={{ marginBottom: 15 }}
              />
            )}
            
            <Alert
              message="Note: Please ensure the CSV file is in the correct format."
              type="info"
              showIcon
              style={{ marginBottom: 15 }}
            />
            
            <Space direction="vertical" style={{ width: '100%' }}>
              <Button type="link" icon={<DownloadOutlined />} onClick={downloadFn} block>
                Download Template
              </Button>
            </Space>
          </Form>

          <div style={{ marginTop: 16, display: 'flex', justifyContent: 'space-between' }}>
            <Button 
              type="default" 
              onClick={openMappingModal}
              disabled={fileList.length === 0}
            >
              Map Columns
            </Button>
            
            <Button 
              type="primary" 
              icon={<EyeOutlined />}
              onClick={openPreviewModal}
              disabled={fileList.length === 0 || Object.keys(fieldMappings).length === 0}
            >
              Preview Mapped Data
            </Button>
          </div>
        </>
      )}
    </>
  );

  return (
    <Layout style={{ minHeight: '125vh' }}>
      <Sidebar collapsed={collapsed} onCollapse={setCollapsed} />
      <Layout className="site-layout">
        <AppHeader />
        <Content style={{ margin: '0 16px' }}>
          <div
            style={{
              background: '#fff',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
              borderRadius: 10,
              padding: 24
            }}
          >
            <Button
              type="primary"
              onClick={showLeadsModal}
              style={{ marginRight: 16 }}
            >
              Import Leads Data
            </Button>
            <Button type="primary" onClick={showCompanyModal}>
              Import Company Data
            </Button>
          </div>
        </Content>
      </Layout>

      <Modal
        title="Import Leads Data"
        visible={isLeadsModalVisible}
        onCancel={handleCancel}
        footer={[
          <Button key="cancel" onClick={handleCancel}>
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            onClick={() => parseAndUpload(uploadEndpoint)}
            loading={loading}
            disabled={loading || Object.keys(fieldMappings).length === 0}
          >
            Import
          </Button>
        ]}
        width={600}
      >
        {renderModalContent(downloadSampleDataPeople)}
      </Modal>

      <Modal
        title="Import Company Data"
        visible={isCompanyModalVisible}
        onCancel={handleCancel}
        footer={[
          <Button key="cancel" onClick={handleCancel}>
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            onClick={() => parseAndUpload(uploadEndpoint)}
            loading={loading}
            disabled={loading || Object.keys(fieldMappings).length === 0}
          >
            Import
          </Button>
        ]}
        width={600}
      >
        {renderModalContent(downloadSampleData)}
      </Modal>

      {renderMappingModal()}
      {renderPreviewModal()}
    </Layout>
  );
}